import { startCase } from 'lodash';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { SHELL_THEMES, ShellThemeSwatch } from '@/features/shell-theme';
import { littlebirdSdk } from '@/lib/sdk-init';
import { useTheme } from '@/providers/ThemeProvider';
import { uiStore } from '@/stores/ui.store';

import { createAction, Section } from '.';

import type { Action } from './types';

export const useThemeActions = () => {
  const { themePreference, setThemePreference } = useTheme();

  const actions: Action[] = useMemo(() => {
    return [
      {
        id: 'dark-theme',
        name: 'Dark',
        selected: () => themePreference === 'dark',
        icon: <Icons.darkTheme size={14} />,
        section: Section.Settings,
        perform: () => setThemePreference('dark'),
      },
      {
        id: 'light-theme',
        name: 'Light',
        selected: () => themePreference === 'light',
        icon: <Icons.lightTheme size={14} />,
        section: Section.Settings,
        perform: () => setThemePreference('light'),
      },
      {
        id: 'system-theme',
        name: 'System',
        selected: () => themePreference === 'system',
        icon: <Icons.systemTheme size={14} />,
        section: Section.Settings,
        perform: () => setThemePreference('system'),
      },
    ];
  }, [setThemePreference, themePreference]);

  const changeColorMode = useMemo(
    () =>
      createAction({
        name: 'Change color mode',
        placeholder: 'Change color mode to',
        icon:
          themePreference === 'light' ? (
            <Icons.lightTheme size={14} />
          ) : themePreference === 'system' ? (
            <Icons.systemTheme size={14} />
          ) : (
            <Icons.darkTheme size={14} />
          ),
        section: Section.Settings,
        children: () => actions,
      }),
    [actions, themePreference]
  );

  const changeTheme = useMemo(
    () =>
      createAction({
        name: 'Change theme',
        placeholder: 'Change theme to',
        icon: <Icons.palette size={14} />,
        section: Section.Settings,
        children: () =>
          SHELL_THEMES.map((preset) => ({
            id: `shell-theme-${preset}`,
            name: startCase(preset),
            selected: () => uiStore.shellTheme === preset,
            icon: <ShellThemeSwatch preset={preset} className="size-3.5" />,
            section: Section.Settings,
            perform: () => uiStore.setShellTheme(preset),
          })),
      }),
    []
  );

  return [changeColorMode, changeTheme];
};

export const resetLocalDatabaseAction = createAction({
  id: 'reset-local-database',
  name: 'Reset local database',
  section: Section.Settings,
  icon: <Icons.reset size={14} />,
  dangerous: true,
  children: [
    {
      section: Section.Settings,
      id: 'reset-local-database-confirm',
      name: 'Yes, reset database',
      icon: <Icons.check size={14} />,
      visible: ({ query }) => !query.length,
      perform: async () => {
        const toastId = toast.loading('Reset local database', {
          description: 'Resetting...',
        });

        try {
          await littlebirdSdk.forceRefresh();
          toast.success('Reset local database', {
            id: toastId,
            description: 'Successfully cleared and refreshed all data.',
          });
        } catch (_error) {
          toast.error('Reset local database', {
            id: toastId,
            description: 'Failed to reset. Please try again.',
          });
        }
      },
    },
    {
      section: Section.Settings,
      id: 'reset-local-database-cancel',
      name: 'Cancel',
      icon: <Icons.cancel size={14} />,
      visible: ({ query }) => !query.length,
      perform: () => {},
    },
  ],
});
